import { hasModuleAccess } from "./accessControl";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DndContext, useDroppable, useDraggable, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./useAuth";
import { RoleEnum, isRole } from "./constants/roles";
import {
  getRisks,
  updateRiskParent,
  getRiskCategories,
  getRiskSnapshots,
  createRiskSnapshot,
  exportRiskSnapshot,
  getRiskSnapshot,
  downloadRiskBulkUploadTemplate,
  importRiskBulkUploadWorkbook,
} from "./riskService";
import RiskDetailDrawer from "./RiskDetailDrawer";
import AddChildRiskForm from "./AddChildRiskForm";
import RiskDetailTabs from "./RiskDetailTabs";
import { getRiskMatrixConfig } from "./api/riskMatrixConfig.api";
import AddRiskForm from "./AddRiskForm";
import Button from "./ui/Button";
import { StatusBadge, Input, PageState, Select } from "./ui";
import ModalA11yWrapper from "./ui/ModalA11yWrapper";
import { colors, radius, shadows, spacing, typography } from "./ui/tokens";
import {
  bandToColor,
  getCellScore,
  getReadableTextColor,
  normalizeMatrixConfig,
  scoreToBand,
} from "./utils/riskMatrixLogic";
import { richTextToPlainText } from "./utils/riskReviewTemplate";

import {
  Shield,
  Settings,
  FileText,
  DollarSign,
  Link,
  User,
  Target,
  Building,
  AlertTriangle,
} from "lucide-react";

// Simple chevron for expand/collapse
const Chevron = ({ open = false }) => (
  <span style={{ display: "inline-block", width: 14 }}>
    {open ? "v" : ">"}
  </span>
);

function DragHandle({ id, disabled, highlighted }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id, disabled });

  return (
    <span
      ref={setNodeRef}
      {...attributes}
      {...(disabled ? {} : listeners)}
      onClick={(e) => e.stopPropagation()}
      style={{
        cursor: disabled ? "not-allowed" : "grab",
        opacity: disabled ? 0.1 : highlighted || isDragging ? 0.9 : 0.25,
        transition: "opacity 0.12s ease",
        padding: `${spacing.xs / 2}px ${spacing.xs}px`,
        borderRadius: 4,
        display: "inline-flex",
      }}
      aria-label="Drag handle"
    >
      *
    </span>
  );
}

const categoryIcons = {
  Cybersecurity: Shield,
  Operations: Settings,
  Compliance: FileText,
  Finance: DollarSign,
  "Third-Party": Link,
  People: User,
  Strategy: Target,
  Facilities: Building,
};
const DefaultIcon = AlertTriangle;
const DEFAULT_COLUMN_ORDER = [
  "category",
  "name",
  "description",
  "severity",
  "status",
  "score",
  "next_review",
  "appetite",
  "treatment",
];
const DEFAULT_COLUMN_WIDTHS = {
  category: 220,
  name: 240,
  description: 320,
  severity: 140,
  status: 180,
  score: 130,
  next_review: 170,
  appetite: 180,
  treatment: 180,
};
const MIN_COLUMN_WIDTHS = {
  category: 160,
  name: 180,
  description: 220,
  severity: 110,
  status: 130,
  score: 100,
  next_review: 130,
  appetite: 130,
  treatment: 130,
};
const MAX_COLUMN_WIDTH = 640;
const COLUMN_WIDTHS_STORAGE_KEY = "riskRegister.columnWidths.v1";
const BULK_UPLOAD_MAX_ROWS = 500;

const sanitizeColumnWidths = (raw) => {
  const next = { ...DEFAULT_COLUMN_WIDTHS };
  if (!raw || typeof raw !== "object") return next;
  Object.keys(DEFAULT_COLUMN_WIDTHS).forEach((colKey) => {
    const candidate = Number(raw[colKey]);
    if (!Number.isFinite(candidate)) return;
    const minWidth = MIN_COLUMN_WIDTHS[colKey] || 120;
    next[colKey] = Math.max(minWidth, Math.min(MAX_COLUMN_WIDTH, candidate));
  });
  return next;
};

const loadStoredColumnWidths = () => {
  if (typeof window === "undefined") return { ...DEFAULT_COLUMN_WIDTHS };
  try {
    const stored = window.localStorage.getItem(COLUMN_WIDTHS_STORAGE_KEY);
    if (!stored) return { ...DEFAULT_COLUMN_WIDTHS };
    const parsed = JSON.parse(stored);
    return sanitizeColumnWidths(parsed);
  } catch (_err) {
    return { ...DEFAULT_COLUMN_WIDTHS };
  }
};

const mergeUpdatedRiskInTree = (items, updatedRisk) => {
  if (!Array.isArray(items) || !updatedRisk?.id) return items;
  const updatedId = Number(updatedRisk.id);
  let mutated = false;

  const next = items.map((item) => {
    if (!item) return item;
    const itemId = Number(item.id || item._id);
    if (itemId === updatedId) {
      mutated = true;
      return { ...item, ...updatedRisk };
    }
    if (Array.isArray(item.children) && item.children.length) {
      const nextChildren = mergeUpdatedRiskInTree(item.children, updatedRisk);
      if (nextChildren !== item.children) {
        mutated = true;
        return { ...item, children: nextChildren };
      }
    }
    return item;
  });

  return mutated ? next : items;
};

export default function RiskRegister() {
  const location = useLocation();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [risks, setRisks] = useState([]);
  const [total, setTotal] = useState(0);
  const [drawerRiskId, setDrawerRiskId] = useState(null);
  // drawerRiskId drives the overlay drawer; selectedRiskId drives the main-panel promotion.
  const [selectedRiskId, setSelectedRiskId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [bulkUploadFileError, setBulkUploadFileError] = useState("");
  const [bulkUploadRequestError, setBulkUploadRequestError] = useState("");
  const [bulkUploadUploading, setBulkUploadUploading] = useState(false);
  const [bulkUploadResult, setBulkUploadResult] = useState(null);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState(null);
  const [matrixConfig, setMatrixConfig] = useState(() => normalizeMatrixConfig(null));
  const isAdmin = isRole(user?.role, RoleEnum.ADMIN);
  const canWriteRisk = hasModuleAccess(user, "risks", "EDIT");
  const canCreateSnapshots = hasModuleAccess(user, "risks", "MANAGE");
  const canViewSnapshots = hasModuleAccess(user, "risks", "VIEW");

  // Filters
  const [severityFilter, setSeverityFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [breachOnly, setBreachOnly] = useState(false);
  const [treatmentOverdueOnly, setTreatmentOverdueOnly] = useState(false);

  // Search
  const [searchQuery, setSearchQuery] = useState("");

  // Column visibility
  const [visibleCols, setVisibleCols] = useState({
    category: true,
    name: true,
    description: false,
    severity: true,
    status: true,
    score: true,
    next_review: false,
    appetite: false,
    treatment: false,
  });
  const [columnOrder, setColumnOrder] = useState(DEFAULT_COLUMN_ORDER);
  const [draggingColumn, setDraggingColumn] = useState(null);
  const [columnWidths, setColumnWidths] = useState(loadStoredColumnWidths);
  const [hasResizedColumnsThisView, setHasResizedColumnsThisView] = useState(false);
  const [tableViewportWidth, setTableViewportWidth] = useState(0);
  const tableViewportRef = useRef(null);

  // Sorting
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [expandedIds, setExpandedIds] = useState(new Set()); // Track expanded parents
  const [childFormParent, setChildFormParent] = useState(null); // {id, name}
  const [hoveredDivider, setHoveredDivider] = useState(null); // Divider hover state for full-width activation
  const [draggingId, setDraggingId] = useState(null);
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [snapshotsOpen, setSnapshotsOpen] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [snapshotsLoading, setSnapshotsLoading] = useState(false);
  const [snapshotsError, setSnapshotsError] = useState(null);
  const [snapshotView, setSnapshotView] = useState(null);
  const [snapshotViewError, setSnapshotViewError] = useState(null);
  const bulkUploadInputRef = useRef(null);

  // -------------------------------
  // LOAD RISKS (server-side pagination/sorting)
  // -------------------------------
  const fetchRisks = useCallback(() => {
    if (!token) return;
    setLoading(true);
    setError(null);
    const params = {
      limit: pageSize,
      offset: (page - 1) * pageSize,
    };
    if (searchQuery.trim()) params.q = searchQuery.trim();
    if (statusFilter !== "All") params.status = statusFilter;
    if (categoryFilter !== "All") params.category = categoryFilter;
    if (overdueOnly) params.overdue_review = true;
    if (breachOnly) params.appetite_breach = true;
    if (treatmentOverdueOnly) params.overdue_treatment = true;
    if (sortField) {
      params.sort = (sortDir === "desc" ? "-" : "") + sortField;
    }
    getRisks(params)
      .then((res) => {
        const body = res?.data || res || {};
        setRisks(body.items || []);
        setTotal(body.total || 0);
      })
      .catch((err) => {
        setError(err?.response?.data?.detail || "Something went wrong.");
        setRisks([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [token, page, pageSize, sortField, sortDir, searchQuery, statusFilter, categoryFilter, overdueOnly, breachOnly, treatmentOverdueOnly]);

  useEffect(() => {
    fetchRisks();
  }, [fetchRisks]);
  useEffect(() => setPage(1), [searchQuery, statusFilter, categoryFilter, overdueOnly, breachOnly, treatmentOverdueOnly, pageSize]);

  const fetchCategories = useCallback(() => {
    if (!token) return Promise.resolve();
    setCategoriesLoading(true);
    setCategoriesError(null);
    return getRiskCategories({ include_archived: false })
      .then((res) => {
        const items = res?.data || res || [];
        setCategories(items);
      })
      .catch((err) => {
        setCategories([]);
        setCategoriesError(err?.response?.data?.error || err?.message || "Unable to load risk categories.");
      })
      .finally(() => setCategoriesLoading(false));
  }, [token]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    if (!token) return;
    let active = true;
    getRiskMatrixConfig()
      .then((res) => {
        if (!active) return;
        setMatrixConfig(normalizeMatrixConfig(res?.data || res));
      })
      .catch(() => {
        if (!active) return;
        setMatrixConfig(normalizeMatrixConfig(null));
      });
    return () => {
      active = false;
    };
  }, [token]);

  useEffect(() => {
    const handleCategoriesChanged = () => {
      fetchCategories();
    };
    window.addEventListener("riskCategoriesChanged", handleCategoriesChanged);
    return () => window.removeEventListener("riskCategoriesChanged", handleCategoriesChanged);
  }, [fetchCategories]);

  useEffect(() => {
    if (!token) {
      setCategories([]);
      setCategoriesLoading(false);
      setCategoriesError(null);
    }
  }, [token]);

  useEffect(() => {
    try {
      window.localStorage.setItem(COLUMN_WIDTHS_STORAGE_KEY, JSON.stringify(columnWidths));
    } catch (_err) {
      // ignore storage errors
    }
  }, [columnWidths]);

  useEffect(() => {
    const selectedFromState = Number(location.state?.selectedRiskId);
    const drawerFromState = Number(location.state?.openDrawerRiskId);
    const hasSelected = Number.isInteger(selectedFromState) && selectedFromState > 0;
    const hasDrawer = Number.isInteger(drawerFromState) && drawerFromState > 0;

    if (!hasSelected && !hasDrawer) return;

    if (hasDrawer) {
      setDrawerRiskId(drawerFromState);
      setSelectedRiskId(null);
    } else {
      setSelectedRiskId(selectedFromState);
      setDrawerRiskId(null);
    }

    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    const measureViewport = () => {
      const node = tableViewportRef.current;
      if (!node) return;
      const nextWidth = Math.round(node.clientWidth || node.getBoundingClientRect().width || 0);
      setTableViewportWidth((prev) => (prev === nextWidth ? prev : nextWidth));
    };

    measureViewport();

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined" && tableViewportRef.current) {
      resizeObserver = new ResizeObserver(() => measureViewport());
      resizeObserver.observe(tableViewportRef.current);
    }

    window.addEventListener("resize", measureViewport);
    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener("resize", measureViewport);
    };
  }, []);

  // NEW - allows RiskDetailDrawer to open another risk
  useEffect(() => {
    const handler = (e) => {
      const id = e.detail;
      setDrawerRiskId(id);
    };
    window.addEventListener("openRiskDrawer", handler);
    return () => window.removeEventListener("openRiskDrawer", handler);
  }, []);

  // Refresh list and clear selection when a risk is deleted elsewhere (e.g., from detail view).
  useEffect(() => {
    const handleDeleted = (e) => {
      const deletedId = e.detail;
      setSelectedRiskId((prev) => (prev === deletedId ? null : prev));
      setDrawerRiskId((prev) => (prev === deletedId ? null : prev));
      fetchRisks();
    };
    window.addEventListener("riskDeleted", handleDeleted);
    return () => window.removeEventListener("riskDeleted", handleDeleted);
  }, [fetchRisks]);

  // Dynamic categories
  const categoryList = useMemo(() => {
    const set = new Set();
    categories.forEach((c) => c?.name && set.add(c.name));
    return Array.from(set);
  }, [categories]);

  const hasCategorySetup = useMemo(() => {
    return !categoriesLoading && (categories || []).length > 0;
  }, [categoriesLoading, categories]);

  const canCreateRisk = useMemo(() => {
    return canWriteRisk && hasCategorySetup;
  }, [canWriteRisk, hasCategorySetup]);

  // Allow external triggers (e.g., sidebar) to open the "Add Risk" modal.
  useEffect(() => {
    const handler = () => {
      if (canCreateRisk) {
        setShowAdd(true);
      }
    };
    window.addEventListener("openAddRisk", handler);
    return () => window.removeEventListener("openAddRisk", handler);
  }, [canCreateRisk]);

  /* --------------------------
     PAGINATION (server-driven)
  -------------------------- */
  const totalPages = Math.max(1, Math.ceil(total / pageSize || 1));

  const displayedRisks = useMemo(() => {
    if (severityFilter === "All") return risks;
    return risks.filter((r) => r.severity === severityFilter);
  }, [risks, severityFilter]);

  const visibleColumnCount = useMemo(
    () => Object.values(visibleCols).filter(Boolean).length,
    [visibleCols]
  );
  const orderedVisibleColumns = useMemo(
    () => columnOrder.filter((colKey) => visibleCols[colKey]),
    [columnOrder, visibleCols]
  );
  const effectiveColumnWidths = useMemo(() => {
    const baseWidths = orderedVisibleColumns.reduce((acc, colKey) => {
      acc[colKey] = columnWidths[colKey] || DEFAULT_COLUMN_WIDTHS[colKey] || 160;
      return acc;
    }, {});

    if (!orderedVisibleColumns.length || hasResizedColumnsThisView || tableViewportWidth <= 0) {
      return baseWidths;
    }

    const totalBaseWidth = orderedVisibleColumns.reduce((totalWidth, colKey) => totalWidth + baseWidths[colKey], 0);
    if (totalBaseWidth >= tableViewportWidth) {
      return baseWidths;
    }

    const extraWidth = tableViewportWidth - totalBaseWidth;
    let distributedWidth = 0;

    return orderedVisibleColumns.reduce((acc, colKey, index) => {
      const isLastColumn = index === orderedVisibleColumns.length - 1;
      const extraForColumn = isLastColumn
        ? extraWidth - distributedWidth
        : Math.round((baseWidths[colKey] / totalBaseWidth) * extraWidth);
      distributedWidth += extraForColumn;
      acc[colKey] = baseWidths[colKey] + extraForColumn;
      return acc;
    }, {});
  }, [columnWidths, hasResizedColumnsThisView, orderedVisibleColumns, tableViewportWidth]);
  const totalVisibleColumnWidth = useMemo(
    () =>
      orderedVisibleColumns.reduce(
        (totalWidth, colKey) => totalWidth + (effectiveColumnWidths[colKey] || DEFAULT_COLUMN_WIDTHS[colKey] || 160),
        0
      ),
    [effectiveColumnWidths, orderedVisibleColumns]
  );

  /* --------------------------
     Sorting Controls
  -------------------------- */
  const toggleSort = (field) => {
    if (sortField !== field) {
      setSortField(field);
      setSortDir("asc");
    } else if (sortDir === "asc") {
      setSortDir("desc");
    } else {
      setSortField(null);
      setSortDir(null);
    }
    setPage(1);
  };

  const sortIndicator = (field) => {
    if (sortField !== field) return "^v";
    return sortDir === "asc" ? "^" : "v";
  };

  const ariaSortFor = (field) => {
    if (sortField !== field) return "none";
    return sortDir === "desc" ? "descending" : "ascending";
  };

  /* --------------------------
     Column Toggles
  -------------------------- */
  const toggleColumn = (col) => {
    setVisibleCols((prev) => ({ ...prev, [col]: !prev[col] }));
  };

  const moveColumn = useCallback((sourceKey, targetKey) => {
    if (!sourceKey || !targetKey || sourceKey === targetKey) return;
    setColumnOrder((prev) => {
      const sourceIndex = prev.indexOf(sourceKey);
      const targetIndex = prev.indexOf(targetKey);
      if (sourceIndex === -1 || targetIndex === -1) return prev;
      const next = [...prev];
      next.splice(sourceIndex, 1);
      next.splice(targetIndex, 0, sourceKey);
      return next;
    });
  }, []);

  const columnMeta = {
    category: { label: "Category", sortField: "category" },
    name: { label: "Name", sortField: "name" },
    description: { label: "Description", sortField: null },
    severity: { label: "Severity", sortField: "severity" },
    status: { label: "Status", sortField: "status" },
    score: { label: "Residual Score", sortField: "residual_score" },
    next_review: { label: "Next Review", sortField: "next_review_date" },
    appetite: { label: "Appetite", sortField: "appetite_status" },
    treatment: { label: "Treatment", sortField: "treatment_status" },
  };

  const startColumnResize = useCallback((event, colKey) => {
    event.preventDefault();
    event.stopPropagation();
    const frozenWidths = orderedVisibleColumns.reduce((acc, visibleColKey) => {
      acc[visibleColKey] = effectiveColumnWidths[visibleColKey] || DEFAULT_COLUMN_WIDTHS[visibleColKey] || 160;
      return acc;
    }, {});
    setColumnWidths((prev) => {
      let next = prev;
      orderedVisibleColumns.forEach((visibleColKey) => {
        const fittedWidth = frozenWidths[visibleColKey];
        if (next[visibleColKey] === fittedWidth) return;
        if (next === prev) {
          next = { ...prev };
        }
        next[visibleColKey] = fittedWidth;
      });
      return next;
    });
    setHasResizedColumnsThisView(true);
    const startX = event.clientX;
    const startWidth = frozenWidths[colKey] || DEFAULT_COLUMN_WIDTHS[colKey] || 160;
    const minWidth = MIN_COLUMN_WIDTHS[colKey] || 120;
    const currentIndex = orderedVisibleColumns.indexOf(colKey);
    const nextColKey = currentIndex >= 0 ? orderedVisibleColumns[currentIndex + 1] : null;
    const nextStartWidth = nextColKey ? frozenWidths[nextColKey] || DEFAULT_COLUMN_WIDTHS[nextColKey] || 160 : 0;
    const nextMinWidth = nextColKey ? MIN_COLUMN_WIDTHS[nextColKey] || 120 : 0;

    const onMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;

      if (nextColKey) {
        const totalPairWidth = startWidth + nextStartWidth;
        const nextWidth = Math.max(minWidth, Math.min(totalPairWidth - nextMinWidth, startWidth + deltaX));
        const resizedNeighborWidth = totalPairWidth - nextWidth;
        setColumnWidths((prev) => {
          if (prev[colKey] === nextWidth && prev[nextColKey] === resizedNeighborWidth) {
            return prev;
          }
          return {
            ...prev,
            [colKey]: nextWidth,
            [nextColKey]: resizedNeighborWidth,
          };
        });
        return;
      }

      const nextWidth = Math.max(minWidth, Math.min(MAX_COLUMN_WIDTH, startWidth + deltaX));
      setColumnWidths((prev) => (
        prev[colKey] === nextWidth ? prev : { ...prev, [colKey]: nextWidth }
      ));
    };

    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  }, [effectiveColumnWidths, orderedVisibleColumns]);

  // Promote a risk into the main content panel while keeping filters/search state in memory.
  const handleOpenInPanel = (id) => {
    if (!id) return;
    setSelectedRiskId(id);
    setDrawerRiskId(null);
  };

  const handleRiskUpdated = useCallback((updatedRisk) => {
    if (updatedRisk?.id) {
      setRisks((prev) => mergeUpdatedRiskInTree(prev, updatedRisk));
    }
    fetchRisks();
  }, [fetchRisks]);

  const handleBackToRegister = () => {
    setSelectedRiskId(null);
  };

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Build a render-friendly list with child rows injected when expanded.
  const buildRowsWithChildren = () => {
    const rows = [];
    displayedRisks.forEach((r) => {
      rows.push({ risk: r, isChild: false, parentId: null });
      if (expandedIds.has(r.id || r._id) && Array.isArray(r.children) && r.children.length) {
        r.children.forEach((child) => {
          rows.push({ risk: child, isChild: true, parentId: r.id || r._id });
        });
      }
    });
    return rows;
  };

  const rowsWithChildren = buildRowsWithChildren();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const dragDisabled = !!drawerRiskId || showAdd || !!childFormParent;

  const riskLookup = useMemo(() => {
    const map = new Map();
    risks.forEach((r) => map.set(r.id || r._id, r));
    return map;
  }, [risks]);

  // Detect cycles: check if possibleDescendantId is within the ancestorId subtree.
  const isDescendant = (ancestorId, possibleDescendantId) => {
    if (!ancestorId || !possibleDescendantId) return false;
    const ancestor = riskLookup.get(ancestorId);
    if (!ancestor || !ancestor.children) return false;
    for (const child of ancestor.children) {
      const cid = child.id || child._id;
      if (cid === possibleDescendantId) return true;
      if (isDescendant(cid, possibleDescendantId)) return true;
    }
    return false;
  };

  const handleDragStart = (event) => {
    if (dragDisabled) return;
    setDraggingId(event.active.id);
  };

  // Drop validation to prevent self/descendant cycles.
  const isValidDrop = (dragId, dropId) => {
    if (!dropId || !dragId) return false;
    if (dragId === dropId) return false;
    // Prevent dropping onto a descendant (cycle).
    return !isDescendant(dragId, dropId);
  };

  const handleDragEnd = async (event) => {
    setDraggingId(null);
    const { active, over } = event || {};
    if (!active || !over) return;
    const dragId = active.id;
    const dropMeta = over.data?.current || {};
    const dropType = dropMeta.type;
    const targetId = dropMeta.riskId || null;

    if (dropType === "row" && !isValidDrop(dragId, targetId)) {
      return;
    }

    // Determine new parent: null for top-level, or target risk id for row drops.
    const newParentId = dropType === "top" ? null : targetId;

    // If drop is visual-only reorder (same parent), we skip backend update (no ordering field today).
    if (dropType === "reorder") {
      return;
    }

    try {
      await updateRiskParent(dragId, newParentId);
      // Keep the target parent expanded so the moved child is visible.
      if (newParentId) {
        setExpandedIds((prev) => {
          const next = new Set(prev);
          next.add(newParentId);
          return next;
        });
      }
      fetchRisks();
    } catch (err) {
      // Backend failure -> no optimistic change applied.
      console.error("Failed to move risk", err);
    }
  };

  const loadSnapshots = () => {
    if (!canViewSnapshots) return;
    setSnapshotsLoading(true);
    setSnapshotsError(null);
    getRiskSnapshots()
      .then((res) => setSnapshots(res?.data || res || []))
      .catch((err) => setSnapshotsError(err?.response?.data?.detail || err?.message || "Unable to load snapshots."))
      .finally(() => setSnapshotsLoading(false));
  };

  const handleCreateSnapshot = () => {
    if (!canCreateSnapshots) return;
    setSnapshotsError(null);
    createRiskSnapshot({ scope: "register" })
      .then(() => loadSnapshots())
      .catch((err) => setSnapshotsError(err?.response?.data?.detail || err?.message || "Unable to create snapshot."));
  };

  const handleViewSnapshot = (snapshotId) => {
    setSnapshotView(null);
    setSnapshotViewError(null);
    getRiskSnapshot(snapshotId)
      .then((res) => setSnapshotView(res?.data || res))
      .catch((err) => setSnapshotViewError(err?.response?.data?.detail || err?.message || "Unable to load snapshot."));
  };

  const handleExportSnapshot = (snapshotId) => {
    exportRiskSnapshot(snapshotId)
      .then((res) => {
        const blob = res instanceof Blob ? res : new Blob([res], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `risk-snapshot-${snapshotId}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => setSnapshotsError(err?.response?.data?.detail || err?.message || "Unable to export snapshot."));
  };

  const handleDownloadBulkUploadTemplate = () => {
    if (!canCreateRisk) return;
    setBulkUploadRequestError("");
    downloadRiskBulkUploadTemplate()
      .then((res) => {
        const mimeType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        const blob = res instanceof Blob ? res : new Blob([res], { type: mimeType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "risk-bulk-upload-template.xlsx";
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        setBulkUploadRequestError(err?.response?.data?.detail || err?.message || "Unable to download bulk upload template.");
      });
  };

  const resetBulkUploadState = useCallback(() => {
    setBulkUploadFile(null);
    setBulkUploadFileError("");
    setBulkUploadRequestError("");
    setBulkUploadUploading(false);
    setBulkUploadResult(null);
    if (bulkUploadInputRef.current) {
      bulkUploadInputRef.current.value = "";
    }
  }, []);

  const handleOpenBulkUpload = () => {
    if (!canCreateRisk) return;
    resetBulkUploadState();
    setBulkUploadOpen(true);
  };

  const handleCloseBulkUpload = () => {
    setBulkUploadOpen(false);
    resetBulkUploadState();
  };

  const handleBulkUploadFileChange = (event) => {
    const selectedFile = event?.target?.files?.[0] || null;
    setBulkUploadFile(selectedFile);
    setBulkUploadFileError("");
    setBulkUploadRequestError("");
    setBulkUploadResult(null);

    if (!selectedFile) return;
    if (!String(selectedFile.name || "").toLowerCase().endsWith(".xlsx")) {
      setBulkUploadFile(null);
      setBulkUploadFileError("Upload a `.xlsx` workbook created from the Glyder risk bulk upload template.");
      event.target.value = "";
    }
  };

  const handleBulkUploadSubmit = async () => {
    if (!canCreateRisk || !bulkUploadFile || bulkUploadFileError) return;
    setBulkUploadUploading(true);
    setBulkUploadRequestError("");
    setBulkUploadResult(null);

    try {
      const result = await importRiskBulkUploadWorkbook(bulkUploadFile);
      setBulkUploadResult(result);
      if (result?.success) {
        fetchRisks();
        setBulkUploadFile(null);
        if (bulkUploadInputRef.current) {
          bulkUploadInputRef.current.value = "";
        }
      }
    } catch (err) {
      setBulkUploadRequestError(err?.response?.data?.detail || err?.message || "Unable to import bulk upload workbook.");
    } finally {
      setBulkUploadUploading(false);
    }
  };

  return (
    <div>
      <h2 style={{ marginTop: spacing.md, marginBottom: spacing.xl, fontSize: typography.size.xl }}>Risk Register</h2>

      {selectedRiskId ? (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: spacing.lg, marginBottom: spacing.xl }}>
            <Button onClick={handleBackToRegister} variant="secondary">
              {"<- Back to Risk Register"}
            </Button>
          </div>
          <div style={{ padding: spacing.xs, border: `1px solid ${colors.border}`, borderRadius: 10, background: colors.surface }}>
            <RiskDetailTabs
              riskId={selectedRiskId}
              onOpenInPanel={handleOpenInPanel}
              onRiskUpdated={handleRiskUpdated}
              mode="page"
            />
          </div>
        </div>
      ) : (
        <>
          {/* ACTION BAR */}
          <div style={riskToolbarShellStyle}>
            <div style={riskToolbarPrimaryRowStyle}>
              <div style={riskToolbarActionGroupStyle}>
                <Button
                  onClick={() => setShowAdd(true)}
                  variant="primary"
                  disabled={!canCreateRisk}
                  style={{ background: colors.brandPrimary, borderWidth: 1, borderStyle: "solid", borderColor: colors.brandPrimary }}
                >
                  + Add Risk
                </Button>
                <Button
                  onClick={handleOpenBulkUpload}
                  variant="secondary"
                  disabled={!canCreateRisk}
                >
                  Bulk Upload
                </Button>
                {canViewSnapshots && (
                  <Button
                    onClick={() => {
                      setSnapshotsOpen(true);
                      loadSnapshots();
                    }}
                    variant="ghost"
                  >
                    Snapshots
                  </Button>
                )}
              </div>

              <div style={riskToolbarUtilityGroupStyle}>
                {/* Column toggle menu */}
                <details style={riskToolbarMenuStyle}>
                  <summary style={riskToolbarSummaryStyle}>Columns</summary>
                  <div style={riskToolbarMenuContentStyle}>
                    {columnOrder.map((colKey) => (
                      <React.Fragment key={`toggle-${colKey}`}>
                        <label style={riskToolbarMenuItemStyle}>
                          <Input
                            type="checkbox"
                            checked={Boolean(visibleCols[colKey])}
                            onChange={() => toggleColumn(colKey)}
                            style={checkboxInputStyle}
                          />{" "}
                          {columnMeta[colKey]?.label || colKey}
                        </label>
                      </React.Fragment>
                    ))}
                  </div>
                </details>
              </div>
            </div>

            <div style={riskToolbarFilterRowStyle}>
              <Input
                placeholder="Search risks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={riskSearchInputStyle}
              />

              <Select style={riskFilterInputStyle} value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)}>
                <option>All</option>
                <option>Critical</option>
                <option>High</option>
                <option>Medium</option>
                <option>Low</option>
                <option>Info</option>
              </Select>

              <Select style={riskFilterInputStyle} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option>All</option>
                <option>Draft</option>
                <option>In Review</option>
                <option>Approved</option>
                <option>Closed</option>
              </Select>

              <Select style={riskFilterInputStyle} value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                <option>All</option>
                {categoryList.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </Select>
            </div>

            <div style={riskToolbarMetaRowStyle}>
              <div style={riskToolbarToggleGroupStyle}>
                <label
                  style={{
                    ...riskToolbarToggleStyle,
                    ...(overdueOnly ? riskToolbarToggleActiveStyle : null),
                  }}
                >
                  <Input
                    type="checkbox"
                    checked={overdueOnly}
                    onChange={(e) => setOverdueOnly(e.target.checked)}
                    style={checkboxInputStyle}
                  />
                  Overdue reviews
                </label>
                <label
                  style={{
                    ...riskToolbarToggleStyle,
                    ...(breachOnly ? riskToolbarToggleActiveStyle : null),
                  }}
                >
                  <Input
                    type="checkbox"
                    checked={breachOnly}
                    onChange={(e) => setBreachOnly(e.target.checked)}
                    style={checkboxInputStyle}
                  />
                  Appetite breaches
                </label>
                <label
                  style={{
                    ...riskToolbarToggleStyle,
                    ...(treatmentOverdueOnly ? riskToolbarToggleActiveStyle : null),
                  }}
                >
                  <Input
                    type="checkbox"
                    checked={treatmentOverdueOnly}
                    onChange={(e) => setTreatmentOverdueOnly(e.target.checked)}
                    style={checkboxInputStyle}
                  />
                  Treatment overdue
                </label>
              </div>

              <div style={riskToolbarHintStyle}>
                Tip: drag column headers to reorder, or drag the divider left or right to resize.
              </div>
            </div>
          </div>

          {loading && (
            <PageState
              state="loading"
              align="left"
              compact
              title="Loading risks"
              description="Glyder is preparing the current risk register, filters, and review signals."
            />
          )}
          {error && (
            <PageState
              state="error"
              align="left"
              compact
              title="Risk register unavailable"
              description={`${error} Refresh the register to try again.`}
              action={
                <Button type="button" variant="secondary" onClick={fetchRisks}>
                  Refresh
                </Button>
              }
            />
          )}
          {!categoriesLoading && categoriesError && (
            <div style={{ color: colors.danger, marginBottom: spacing.md }}>
              {categoriesError}
            </div>
          )}
          {!categoriesLoading && !categoriesError && !hasCategorySetup && (
            <div style={emptyCategoryBanner}>
              <div style={{ fontWeight: typography.weight.medium }}>Risk categories are not configured</div>
              <div style={{ fontSize: typography.size.sm, color: colors.textMuted }}>
                {isAdmin
                  ? "Create risk categories to enable risk intake for your tenant."
                  : "Risk categories must be configured by an administrator before risks can be created."}
              </div>
              {isAdmin && (
                <Button
                  onClick={() => window.dispatchEvent(new CustomEvent("navigateModule", { detail: "admin-risk-categories" }))}
                  variant="secondary"
                  style={{ marginTop: spacing.sm }}
                >
                  Set up Risk Categories
                </Button>
              )}
            </div>
          )}
          {!categoriesLoading && !categoriesError && hasCategorySetup && !canWriteRisk && (
            <div style={emptyCategoryBanner}>
              <div style={{ fontWeight: typography.weight.medium }}>Read-only access</div>
              <div style={{ fontSize: typography.size.sm, color: colors.textMuted }}>
                You can view the Risk Register, but you do not have permission to add or bulk upload risks.
              </div>
            </div>
          )}

          {/* TABLE with DnD context scoped to body */}
          <div ref={tableViewportRef} style={{ maxHeight: "70vh", overflowX: "auto", overflowY: "auto" }}>
            <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
              <table
                style={{
                  ...tableStyle,
                  width: `${totalVisibleColumnWidth}px`,
                }}
              >
                <colgroup>
                  {orderedVisibleColumns.map((colKey) => (
                    <col
                      key={`col-${colKey}`}
                      style={{
                        width: `${effectiveColumnWidths[colKey] || DEFAULT_COLUMN_WIDTHS[colKey] || 160}px`,
                        minWidth: `${MIN_COLUMN_WIDTHS[colKey] || 120}px`,
                      }}
                    />
                  ))}
                </colgroup>
                <thead style={stickyHeaderStyle}>
                  <tr>
                    {orderedVisibleColumns.map((colKey) => {
                      const meta = columnMeta[colKey] || {};
                      const sortKey = meta.sortField || null;
                      const sortable = Boolean(sortKey);
                      return (
                        <th
                          key={`header-${colKey}`}
                          style={{
                            ...thStyle,
                            position: "relative",
                            width: `${effectiveColumnWidths[colKey] || DEFAULT_COLUMN_WIDTHS[colKey] || 160}px`,
                            minWidth: `${MIN_COLUMN_WIDTHS[colKey] || 120}px`,
                            cursor: sortable ? "pointer" : "default",
                            opacity: draggingColumn === colKey ? 0.55 : 1,
                          }}
                          draggable
                          onDragStart={(event) => {
                            setDraggingColumn(colKey);
                            event.dataTransfer.effectAllowed = "move";
                            event.dataTransfer.setData("text/plain", colKey);
                          }}
                          onDragOver={(event) => {
                            if (!draggingColumn || draggingColumn === colKey) return;
                            event.preventDefault();
                            event.dataTransfer.dropEffect = "move";
                          }}
                          onDrop={(event) => {
                            event.preventDefault();
                            const source = draggingColumn || event.dataTransfer.getData("text/plain");
                            moveColumn(source, colKey);
                            setDraggingColumn(null);
                          }}
                          onDragEnd={() => setDraggingColumn(null)}
                          onClick={() => sortable && toggleSort(sortKey)}
                          onKeyDown={(event) => {
                            if (!sortable) return;
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              toggleSort(sortKey);
                            }
                          }}
                          tabIndex={sortable ? 0 : -1}
                          aria-sort={sortable ? ariaSortFor(sortKey) : "none"}
                          title="Drag to reorder columns"
                        >
                          <div style={{ paddingRight: 18 }}>
                            <span style={{ marginRight: 6, color: colors.textMuted }}>:</span>
                            {meta.label || colKey}
                            {sortable ? ` ${sortIndicator(sortKey)}` : ""}
                          </div>
                          <div
                            onMouseDown={(event) => startColumnResize(event, colKey)}
                            onClick={(event) => event.stopPropagation()}
                            role="separator"
                            aria-label={`Resize ${meta.label || colKey} column left or right`}
                            style={columnResizeHandleStyle}
                            title="Drag left or right to resize"
                          >
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 3,
                                opacity: 0.7,
                              }}
                            >
                              <span style={columnResizeArrowLeftStyle} />
                              <span style={columnResizeGripLineStyle} />
                              <span style={columnResizeArrowRightStyle} />
                            </span>
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                <tbody>
                  {/* Top-level drop zone to promote to root */}
                  <TopLevelDropZone colSpan={Object.values(visibleCols).filter(Boolean).length} />

                  {rowsWithChildren.map((item, index) => {
                    const r = item.risk;
                    const id = r.id || r._id;
                    const Icon = categoryIcons[r.category] || DefaultIcon;
                    const residualLikelihood = Number(r.residual_likelihood);
                    const residualImpact = Number(r.residual_impact);
                    const hasResidualLevels =
                      Number.isFinite(residualLikelihood) && Number.isFinite(residualImpact);
                    const matrixDerivedScore = hasResidualLevels
                      ? getCellScore({
                          scoringMode: matrixConfig?.scoring_mode,
                          cellScores: matrixConfig?.cell_scores,
                          lik: residualLikelihood,
                          imp: residualImpact,
                        })
                      : null;
                    const scoreValue = Number(r.residual_score);
                    const score =
                      matrixDerivedScore != null
                        ? matrixDerivedScore
                        : Number.isFinite(scoreValue)
                          ? scoreValue
                          : null;
                    const scoreBand = scoreToBand({ score, bandThresholds: matrixConfig?.band_thresholds });
                    const scoreColor = bandToColor({ band: scoreBand, bandColors: matrixConfig?.band_colors });
                    const scoreTextColor = getReadableTextColor(scoreColor);
                    const hasChildren = Array.isArray(r.children) && r.children.length > 0;
                    const expanded = expandedIds.has(id);
                    const nextReviewDate = r.next_review_date ? new Date(r.next_review_date) : null;
                    const overdue = nextReviewDate && nextReviewDate.getTime() < Date.now();
                    const appetiteStatus = r.appetite_status || (r.appetite_breach ? "Exceeds Appetite" : "Within Appetite");
                    const treatmentStatus = r.treatment_status || "-";

                    // Divider appears before each row (except first) to add child risk quickly.
                    // The enclosing div stretches full width; hover anywhere across it reveals the "+" button
                    // without intercepting row clicks above/below (button-only activation keeps main row behavior intact).
                    const divider = index > 0 ? (
                      <tr key={`divider-${id}`}>
                        <td colSpan={Object.values(visibleCols).filter(Boolean).length} style={{ padding: 0 }}>
                          <div
                            style={{
                              position: "relative",
                              height: 12,
                              margin: 0,
                              background: hoveredDivider === id ? colors.surfaceMuted : "transparent",
                              borderTop: hoveredDivider === id ? `1px dashed ${colors.borderStrong}` : "1px dashed transparent",
                              borderBottom: hoveredDivider === id ? `1px dashed ${colors.borderStrong}` : "1px dashed transparent",
                            }}
                            onMouseEnter={() => setHoveredDivider(id)}
                            onMouseLeave={() => setHoveredDivider(null)}
                          >
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                setChildFormParent({ id: rowsWithChildren[index - 1].risk.id || rowsWithChildren[index - 1].risk._id, name: rowsWithChildren[index - 1].risk.name });
                              }}
                              title="Add child risk"
                              variant="secondary"
                              style={{
                                position: "absolute",
                                left: "50%",
                                top: "50%",
                                transform: "translate(-50%, -50%)",
                                width: 24,
                                height: 24,
                                borderRadius: "50%",
                                border: `1px solid ${colors.border}`,
                                background: colors.surface,
                                opacity: hoveredDivider === id ? 1 : 0,
                                transition: "opacity 0.15s ease",
                                padding: 0,
                                boxShadow: "none",
                                zIndex: 1,
                              }}
                              onMouseDown={(e) => e.stopPropagation()}
                              onFocus={(e) => {
                                setHoveredDivider(id);
                                e.currentTarget.style.opacity = 1;
                              }}
                              onBlur={(e) => {
                                setHoveredDivider(null);
                                e.currentTarget.style.opacity = 0;
                              }}
                              onMouseEnter={() => setHoveredDivider(id)}
                              onMouseLeave={() => setHoveredDivider(null)}
                            >
                              +
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ) : null;

                    return (
                      <React.Fragment key={`row-${id}-${index}`}>
                        {divider}
                        <DroppableRow
                          id={id}
                          isValidTarget={draggingId ? isValidDrop(draggingId, id) : true}
                          onClick={() => setDrawerRiskId(id)}
                          isChild={item.isChild}
                          hasChildren={hasChildren}
                          expanded={expanded}
                          toggleExpanded={() => toggleExpanded(id)}
                          onMouseEnter={() => setHoveredRowId(id)}
                          onMouseLeave={() => setHoveredRowId(null)}
                          ariaLabel={`Open risk ${r.name || id}`}
                        >
                          {orderedVisibleColumns.map((colKey) => {
                            if (colKey === "category") {
                              return (
                                <td key={`${id}-category`} style={tdStyle}>
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: spacing.md,
                                      paddingLeft: item.isChild ? spacing.md * 2 : 0,
                                    }}
                                  >
                                    <div style={{ display: "inline-flex", alignItems: "center", gap: spacing.sm }}>
                                      <DragHandle id={id} disabled={dragDisabled} highlighted={hoveredRowId === id} />
                                      {!item.isChild && hasChildren && (
                                        <span
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleExpanded(id);
                                          }}
                                          style={{ cursor: "pointer" }}
                                        >
                                          <Chevron open={expanded} />
                                        </span>
                                      )}
                                      {item.isChild && <span style={{ width: 14 }} />}
                                      <Icon size={16} /> {r.category}
                                    </div>
                                  </div>
                                </td>
                              );
                            }
                            if (colKey === "name") {
                              return (
                                <td
                                  key={`${id}-name`}
                                  style={{
                                    ...tdStyle,
                                    fontWeight: typography.weight.medium,
                                    paddingLeft: item.isChild ? spacing.md * 2 : tdStyle.padding,
                                  }}
                                >
                                  {r.name}
                                </td>
                              );
                            }
                            if (colKey === "description") {
                              const descriptionText = richTextToPlainText(r.description || "");
                              return <td key={`${id}-description`} style={tdStyle}>{descriptionText || "-"}</td>;
                            }
                            if (colKey === "severity") {
                              return (
                                <td key={`${id}-severity`} style={tdStyle}>
                                  <StatusBadge variant="severity" value={r.severity} />
                                </td>
                              );
                            }
                            if (colKey === "status") {
                              return (
                                <td key={`${id}-status`} style={tdStyle}>
                                  <div style={{ display: "flex", gap: spacing.sm, alignItems: "center" }}>
                                    <StatusBadge variant="status" value={r.status} />
                                    {overdue && <StatusBadge variant="status" value="Overdue" />}
                                  </div>
                                </td>
                              );
                            }
                            if (colKey === "score") {
                              return (
                                <td key={`${id}-score`} style={tdStyle}>
                                  <span
                                    data-testid={`risk-score-chip-${id}`}
                                    style={{
                                      padding: `${spacing.sm / 2}px ${spacing.lg}px`,
                                      borderRadius: 999,
                                      background: scoreColor,
                                      color: scoreTextColor,
                                      fontSize: typography.size.xs,
                                      fontWeight: typography.weight.medium,
                                    }}
                                  >
                                    {score ?? "-"}
                                  </span>
                                </td>
                              );
                            }
                            if (colKey === "next_review") {
                              return (
                                <td key={`${id}-next-review`} style={tdStyle}>
                                  {nextReviewDate ? nextReviewDate.toLocaleDateString() : "-"}
                                </td>
                              );
                            }
                            if (colKey === "appetite") {
                              return (
                                <td key={`${id}-appetite`} style={tdStyle}>
                                  <StatusBadge variant="status" value={appetiteStatus} />
                                </td>
                              );
                            }
                            if (colKey === "treatment") {
                              return (
                                <td key={`${id}-treatment`} style={tdStyle}>
                                  {treatmentStatus === "-" ? "-" : <StatusBadge variant="status" value={treatmentStatus} />}
                                </td>
                              );
                            }
                            return null;
                          })}
                        </DroppableRow>
                      </React.Fragment>
                    );
                  })}

                  {!loading && displayedRisks.length === 0 && (
                    <tr>
                      <td
                        colSpan={Math.max(visibleColumnCount, 1)}
                        style={{ padding: spacing.xl, textAlign: "center" }}
                      >
                        {total === 0 ? (
                          <div style={{ display: "grid", gap: spacing.md, justifyItems: "center" }}>
                            <div style={{ fontWeight: typography.weight.medium }}>
                              {canCreateRisk ? "No risks yet" : hasCategorySetup ? "Read-only access" : "Risk setup required"}
                            </div>
                            <div style={{ fontSize: typography.size.sm, color: colors.textMuted, maxWidth: 520 }}>
                              {canCreateRisk
                                ? "Risks capture what could impact objectives. Add your first risk to start tracking exposure."
                                : hasCategorySetup
                                  ? "You can view risks, but you do not have permission to create or bulk upload them."
                                  : isAdmin
                                  ? "Create your first risk categories to enable the Risk Register for this tenant."
                                  : "Risk categories must be configured by an administrator before risks can be created."}
                            </div>
                            {canCreateRisk ? (
                              <Button
                                onClick={() => setShowAdd(true)}
                                variant="primary"
                                style={{ background: colors.brandPrimary, borderWidth: 1, borderStyle: "solid", borderColor: colors.brandPrimary }}
                              >
                                Add Risk
                              </Button>
                            ) : (
                              !hasCategorySetup && isAdmin && (
                                <Button
                                  onClick={() => window.dispatchEvent(new CustomEvent("navigateModule", { detail: "admin-risk-categories" }))}
                                  variant="secondary"
                                >
                                  Set up Risk Categories
                                </Button>
                              )
                            )}
                          </div>
                        ) : (
                          "No matching risks."
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </DndContext>
          </div>

          {/* PAGINATION BAR */}
          <div style={paginationBarStyle}>
            <div>
              Page {page} of {totalPages}
            </div>

            <div style={{ display: "flex", gap: spacing.md }}>
              <Button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                variant="secondary"
              >
                {"<- Prev"}
              </Button>

              <Button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                variant="secondary"
              >
                Next ->
              </Button>
            </div>

            <Select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              style={inputStyle}
            >
              <option value={10}>10 / page</option>
              <option value={20}>20 / page</option>
              <option value={50}>50 / page</option>
              <option value={100}>100 / page</option>
            </Select>
          </div>
        </>
      )}

      {snapshotsOpen && (
        <ModalA11yWrapper open={snapshotsOpen} onClose={() => setSnapshotsOpen(false)}>
          <div style={{ background: colors.surface, borderRadius: 12, padding: 16, width: "720px", maxWidth: "90vw" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: typography.weight.bold }}>Risk Snapshots</div>
                <div style={{ fontSize: typography.size.xs, color: colors.textMuted }}>Snapshot - read-only</div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                {canCreateSnapshots && (
                  <Button onClick={handleCreateSnapshot} variant="primary">
                    Generate Snapshot
                  </Button>
                )}
                <Button onClick={() => setSnapshotsOpen(false)} variant="secondary">
                  Close
                </Button>
              </div>
            </div>
            {snapshotsLoading && (
              <PageState
                state="loading"
                align="left"
                compact
                title="Loading snapshots"
                description="Glyder is preparing the latest register snapshots for this workspace."
                style={{ marginTop: 12 }}
              />
            )}
            {snapshotsError && (
              <PageState
                state="error"
                align="left"
                compact
                title="Snapshots are unavailable"
                description={`${snapshotsError} Refresh to try again.`}
                action={
                  <Button type="button" variant="secondary" onClick={loadSnapshots}>
                    Refresh
                  </Button>
                }
                style={{ marginTop: 12 }}
              />
            )}
            {!snapshotsLoading && !snapshotsError && snapshots.length === 0 && (
              <div style={{ marginTop: 12, color: colors.textMuted }}>No snapshots yet.</div>
            )}
            {!snapshotsLoading && snapshots.length > 0 && (
              <div style={{ marginTop: 12, maxHeight: 260, overflowY: "auto" }}>
                {snapshots.map((snap) => (
                  <div
                    key={snap.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px 0",
                      borderBottom: `1px solid ${colors.border}`,
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: typography.weight.medium }}>#{snap.id} - {snap.scope}</div>
                      <div style={{ fontSize: typography.size.xs, color: colors.textMuted }}>
                        {snap.snapshot_at ? new Date(snap.snapshot_at).toLocaleString() : "-"} - {snap.created_by || "system"}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <Button onClick={() => handleViewSnapshot(snap.id)} variant="secondary">
                        View
                      </Button>
                      <Button onClick={() => handleExportSnapshot(snap.id)} variant="secondary">
                        Export
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {snapshotViewError && <div style={{ color: colors.danger, marginTop: 12 }}>{snapshotViewError}</div>}
            {snapshotView && (
              <div
                style={{
                  marginTop: 12,
                  padding: 12,
                  background: colors.surfaceSubtle,
                  borderRadius: 8,
                  border: `1px solid ${colors.border}`,
                }}
              >
                <div style={{ fontSize: typography.size.xs, color: colors.textMuted, marginBottom: 6 }}>
                  Snapshot hash: {snapshotView.payload_hash}
                </div>
                <pre style={{ margin: 0, fontSize: typography.size.xs, maxHeight: 200, overflowY: "auto" }}>
                  {JSON.stringify(snapshotView.payload, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </ModalA11yWrapper>
      )}

      {bulkUploadOpen && (
        <ModalA11yWrapper open={bulkUploadOpen} onClose={handleCloseBulkUpload}>
          <div style={{ background: colors.surface, borderRadius: 12, padding: 16, width: "640px", maxWidth: "92vw" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <div>
                <div style={{ fontWeight: typography.weight.bold }}>Bulk Upload Risks</div>
                <div style={{ fontSize: typography.size.xs, color: colors.textMuted }}>
                  Use the existing template. `.xlsx` only, up to {BULK_UPLOAD_MAX_ROWS} populated rows.
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <Button onClick={handleDownloadBulkUploadTemplate} variant="secondary" disabled={bulkUploadUploading}>
                  Download Template
                </Button>
                <Button onClick={handleCloseBulkUpload} variant="secondary">
                  Close
                </Button>
              </div>
            </div>

            <div style={{ marginTop: 16, display: "grid", gap: 12 }}>
              <label style={{ display: "grid", gap: 6 }}>
                <span style={{ fontWeight: typography.weight.medium }}>Completed Workbook</span>
                <input
                  ref={bulkUploadInputRef}
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={handleBulkUploadFileChange}
                  disabled={bulkUploadUploading}
                />
              </label>

              <div style={{ fontSize: typography.size.xs, color: colors.textMuted }}>
                Empty rows are ignored. The import validates every populated row before creating any risks.
              </div>

              {bulkUploadFile && (
                <div style={{ fontSize: typography.size.sm }}>
                  Selected file: <strong>{bulkUploadFile.name}</strong>
                </div>
              )}
              {bulkUploadFileError && <div style={{ color: colors.danger }}>{bulkUploadFileError}</div>}
              {bulkUploadRequestError && <div style={{ color: colors.danger }}>{bulkUploadRequestError}</div>}

              <div style={{ display: "flex", gap: 8 }}>
                <Button
                  onClick={handleBulkUploadSubmit}
                  variant="primary"
                  disabled={!bulkUploadFile || !!bulkUploadFileError || bulkUploadUploading}
                  style={{ background: colors.brandPrimary, borderWidth: 1, borderStyle: "solid", borderColor: colors.brandPrimary }}
                >
                  {bulkUploadUploading ? "Uploading..." : "Upload Completed File"}
                </Button>
              </div>

              {bulkUploadResult && (
                <div style={{ border: `1px solid ${colors.border}`, borderRadius: 10, padding: 12, display: "grid", gap: 10, background: colors.surface }}>
                  <div style={{ fontWeight: typography.weight.bold }}>
                    {bulkUploadResult.success ? "Import completed" : "Import not completed"}
                  </div>
                  {bulkUploadResult.message && (
                    <div style={{ fontSize: typography.size.sm, color: bulkUploadResult.success ? colors.success : colors.textMuted }}>
                      {bulkUploadResult.message}
                    </div>
                  )}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 12, fontSize: typography.size.sm }}>
                    <span>Processed: {bulkUploadResult.rows_processed || 0}</span>
                    <span>Valid: {bulkUploadResult.rows_valid || 0}</span>
                    <span>Created: {bulkUploadResult.rows_created || 0}</span>
                    <span>Failed: {bulkUploadResult.rows_failed || 0}</span>
                    <span>Empty rows skipped: {bulkUploadResult.rows_skipped_empty || 0}</span>
                  </div>

                  {Array.isArray(bulkUploadResult.file_errors) && bulkUploadResult.file_errors.length > 0 && (
                    <div>
                      <div style={{ fontWeight: typography.weight.medium, marginBottom: 4 }}>File errors</div>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {bulkUploadResult.file_errors.map((item, index) => (
                          <li key={`file-error-${index}`}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {Array.isArray(bulkUploadResult.header_errors) && bulkUploadResult.header_errors.length > 0 && (
                    <div>
                      <div style={{ fontWeight: typography.weight.medium, marginBottom: 4 }}>Header errors</div>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {bulkUploadResult.header_errors.map((item, index) => (
                          <li key={`header-error-${index}`}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {Array.isArray(bulkUploadResult.row_errors) && bulkUploadResult.row_errors.length > 0 && (
                    <div>
                      <div style={{ fontWeight: typography.weight.medium, marginBottom: 4 }}>Row errors</div>
                      <div style={{ maxHeight: 220, overflowY: "auto", border: `1px solid ${colors.border}`, borderRadius: 8, padding: 8 }}>
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                          {bulkUploadResult.row_errors.map((item, index) => (
                            <li key={`row-error-${index}`}>
                              Row {item.row_number}
                              {item.field ? ` - ${item.field}` : ""}
                              : {item.message}
                              {item.value ? ` (${item.value})` : ""}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </ModalA11yWrapper>
      )}

      {/* ADD RISK MODAL */}
      {showAdd && (
        <AddRiskForm
          onClose={() => setShowAdd(false)}
          onSaved={() => {
            setShowAdd(false);
            fetchRisks();
          }}
        />
      )}
      {childFormParent && (
        <AddChildRiskForm
          parentId={childFormParent.id}
          onClose={() => setChildFormParent(null)}
          onSaved={() => {
            setChildFormParent(null);
            fetchRisks();
            setExpandedIds((prev) => {
              const next = new Set(prev);
              next.add(childFormParent.id);
              return next;
            });
          }}
        />
      )}

      {/* DRAWER */}
      <RiskDetailDrawer
        riskId={drawerRiskId}
        onOpenInPanel={handleOpenInPanel}
        onRiskUpdated={handleRiskUpdated}
        onClose={() => setDrawerRiskId(null)}
        onOpen={(id) => setDrawerRiskId(id)} // NEW
      />
    </div>
  );
}

/* ---------- Styles ---------- */

const inputStyle = {
  padding: `${spacing.md}px ${spacing.xl}px`,
  borderRadius: radius.xs,
  border: `1px solid ${colors.borderStrong}`,
  fontSize: typography.size.base,
  background: colors.surface,
  color: colors.textPrimary,
};

const riskSearchInputStyle = {
  ...inputStyle,
  flex: "1 1 280px",
  minWidth: 240,
};

const riskFilterInputStyle = {
  ...inputStyle,
  width: 180,
  maxWidth: "100%",
};

const checkboxInputStyle = {
  width: 16,
  height: 16,
  padding: 0,
  margin: 0,
  borderRadius: 4,
  borderColor: colors.borderStrong,
  accentColor: colors.brandPrimary,
  flex: "0 0 auto",
};

const riskToolbarShellStyle = {
  marginBottom: spacing.xl,
  display: "grid",
  gap: spacing.sm,
  padding: `${spacing.md}px ${spacing.xl}px`,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.xl,
  background: colors.surface,
  boxShadow: shadows.xs,
};

const riskToolbarPrimaryRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: spacing.md,
  flexWrap: "wrap",
};

const riskToolbarActionGroupStyle = {
  display: "flex",
  gap: spacing.sm,
  flexWrap: "wrap",
  alignItems: "center",
};

const riskToolbarUtilityGroupStyle = {
  display: "flex",
  gap: spacing.sm,
  flexWrap: "wrap",
  alignItems: "center",
};

const riskToolbarFilterRowStyle = {
  display: "flex",
  gap: spacing.md,
  flexWrap: "wrap",
  alignItems: "flex-end",
};

const riskToolbarMetaRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: spacing.md,
  flexWrap: "wrap",
};

const riskToolbarToggleGroupStyle = {
  display: "flex",
  gap: spacing.sm,
  flexWrap: "wrap",
  alignItems: "center",
};

const riskToolbarToggleStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: spacing.sm,
  padding: `${spacing.sm}px ${spacing.md}px`,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.pill,
  background: colors.surfaceSubtle,
  color: colors.textSecondary,
  fontSize: typography.size.sm,
  cursor: "pointer",
};

const riskToolbarToggleActiveStyle = {
  background: colors.accentSubtle,
  borderColor: colors.accentBorder,
  color: colors.brandPrimary,
};

const riskToolbarHintStyle = {
  color: colors.textMuted,
  fontSize: typography.size.xs,
  display: "flex",
  justifyContent: "flex-end",
};

const riskToolbarMenuStyle = {
  position: "relative",
};

const riskToolbarSummaryStyle = {
  cursor: "pointer",
  userSelect: "none",
  listStyle: "none",
  display: "inline-flex",
  alignItems: "center",
  gap: spacing.xs,
  padding: `${spacing.sm}px ${spacing.md}px`,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  background: colors.surface,
  color: colors.textSecondary,
  fontSize: typography.size.sm,
};

const riskToolbarMenuContentStyle = {
  position: "absolute",
  top: "calc(100% + 8px)",
  right: 0,
  zIndex: 20,
  minWidth: 220,
  padding: spacing.md,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.lg,
  background: colors.surface,
  boxShadow: shadows.md,
  display: "grid",
  gap: spacing.sm,
};

const riskToolbarMenuItemStyle = {
  display: "flex",
  alignItems: "center",
  gap: spacing.sm,
  fontSize: typography.size.sm,
  color: colors.textSecondary,
};

const tableStyle = {
  borderCollapse: "collapse",
  background: colors.surface,
  tableLayout: "fixed",
};

const stickyHeaderStyle = {
  position: "sticky",
  top: 0,
  zIndex: 10,
  background: colors.tableHeader,
  boxShadow: shadows.xs,
};

const thStyle = {
  padding: `${spacing.md}px ${spacing.md + spacing.sm}px`,
  fontSize: typography.size.xs,
  fontWeight: typography.weight.medium,
  background: "inherit",
  cursor: "pointer",
  userSelect: "none",
  borderBottom: `1px solid ${colors.border}`,
};

const columnResizeHandleStyle = {
  position: "absolute",
  top: 0,
  right: 0,
  width: 16,
  height: "100%",
  cursor: "col-resize",
  userSelect: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const columnResizeGripLineStyle = {
  width: 1,
  height: "72%",
  borderRadius: 999,
  background: colors.textSubtle,
};

const columnResizeArrowLeftStyle = {
  width: 0,
  height: 0,
  borderTop: "4px solid transparent",
  borderBottom: "4px solid transparent",
  borderRight: `4px solid ${colors.textSubtle}`,
};

const columnResizeArrowRightStyle = {
  width: 0,
  height: 0,
  borderTop: "4px solid transparent",
  borderBottom: "4px solid transparent",
  borderLeft: `4px solid ${colors.textSubtle}`,
};

const tdStyle = {
  padding: `${spacing.sm}px ${spacing.md + spacing.sm}px`,
  fontSize: typography.size.sm,
  borderBottom: `1px solid ${colors.border}`,
};

const rowStyle = {
  cursor: "pointer",
  transition: "0.15s ease",
};

const paginationBarStyle = {
  marginTop: spacing.xl,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: `${spacing.sm}px ${spacing.xs}px`,
};

const emptyCategoryBanner = {
  padding: spacing.md,
  borderRadius: 12,
  border: `1px solid ${colors.border}`,
  background: colors.surfaceSubtle,
  display: "grid",
  gap: spacing.xs,
  marginBottom: spacing.lg,
};

/* ---------------- DnD helper components ---------------- */
function DroppableRow({ id, isValidTarget, onClick, isChild, hasChildren, expanded, toggleExpanded, children, onMouseEnter, onMouseLeave, ariaLabel }) {
  const { isOver, setNodeRef } = useDroppable({
    id: `drop-${id}`,
    data: { type: "row", riskId: id },
    disabled: !isValidTarget,
  });

  const handleKeyDown = (event) => {
    if (!onClick) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick(event);
    }
  };

  return (
    <tr
      ref={setNodeRef}
      style={{
        ...rowStyle,
        background: isChild ? colors.surfaceSubtle : colors.surface,
        outline: isOver && isValidTarget ? `2px solid ${colors.brandPrimary}` : "none",
        outlineOffset: -2,
      }}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
    </tr>
  );
}

function TopLevelDropZone({ colSpan }) {
  const { setNodeRef, isOver } = useDroppable({ id: "top-level-zone", data: { type: "top" } });
  return (
    <tr>
      <td colSpan={colSpan} ref={setNodeRef} style={{ padding: spacing.sm }}>
        <div
          style={{
            height: 10,
            borderRadius: 6,
            border: `1px dashed ${colors.borderStrong}`,
            background: isOver ? colors.accentSubtle : "transparent",
            color: colors.textMuted,
            fontSize: typography.size.xs,
            textAlign: "center",
          }}
        >
          {isOver ? "Drop to promote to top-level" : ""}
        </div>
      </td>
    </tr>
  );
}
